<script setup lang="ts">
import { computed, ref } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import LineChartUpIcon from "@/assets/icons/line-chart-up.svg?component";
import { TCategorizedDataResponseSerie } from "@/components/ui/chart/types/categorized-chart-series.type";
import { EMainConsumptionSource } from "@/data/api/kili/consumption/types/common-consumption.type";
import { EColumnPeriodComparisonConsumption } from "@/data/api/kili/consumption/types/consumption-metrics.type";
import { keyOf, useTypedIntl } from "@/locales/typed-intl";
import { useDashboardStore } from "@/stores/dashboard/dashboard-store";
import { YAxisAdapter } from "@/utils/chart/adapters/axis/y-axis-adapter";
import { ColumnSeriesAdapter, IColumnGradientColors } from "@/utils/chart/adapters/series/column-series-adapter";
import { getWhSeriesHighestPrefixSymbol } from "@/utils/chart/chart-series-utils";
import { IColumnChartSpecs } from "@/utils/chart/column-chart-options-utils";
import { assertUnreachable } from "@/utils/types-utils";

import DashboardCard from "@/components/data/dashboard/dashboard-card.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseColumnChart from "@/components/ui/chart/base-column-chart.vue";

interface IProps {
  series?: TCategorizedDataResponseSerie<EColumnPeriodComparisonConsumption>[];
  isLoading?: boolean;
  type: EDashboardType;
  source?: EMainConsumptionSource;
  hasNoData?: boolean;
}

const props = defineProps<IProps>();

const { t, k } = useTypedIntl();
const dashboardStore = useDashboardStore();

const isInfosDialogVisible = ref<boolean>(false);

const currentPeriodConsumptionColumnColor = computed<IColumnGradientColors>(() => {
  return props.type === EDashboardType.MONOSITE
    ? {
        firstStop: "#7FB9FF",
        secondStop: "#CCE0FF",
      }
    : {
        firstStop: "#66E9D3",
        secondStop: "#C8F7EE",
      };
});

const columnChartSpecs = computed<IColumnChartSpecs<EColumnPeriodComparisonConsumption> | undefined>(() => {
  if (!props.series) return undefined;

  const maybePrefixSymbol = getWhSeriesHighestPrefixSymbol(props.series);

  const accessibilityDescription: string = ((): string => {
    switch (props.source) {
      case EMainConsumptionSource.DSO:
        return t(
          k.data.dashboard.consumptionComparisonChart.tooltip.DSO({
            sitesConsumption: t(k.data.dashboard.insights.totalConsumption.title[props.type]),
          }),
        );
      case EMainConsumptionSource.DEVICES:
        return t(
          k.data.dashboard.consumptionComparisonChart.tooltip.DEVICES({
            equipmentConsumption: t(k.data.dashboard.insights.equipmentConsumption.title),
          }),
        );
      case undefined:
        return t(k.data.dashboard.consumptionComparisonChart.tooltip.fallback);
      default:
        return assertUnreachable(props.source);
    }
  })();

  return {
    seriesAdapter: [
      [
        EColumnPeriodComparisonConsumption.LAST_PERIOD_CONSUMPTION,
        ColumnSeriesAdapter({
          name: `${new Date().getFullYear() - 1}`,
          colorGradient: {
            firstStop: "var(--color-border-subtle)",
            secondStop: "#F0F0F4",
          },
          prefixSymbol: maybePrefixSymbol,
          accessibilityDescription: t(k.data.dashboard.consumptionComparisonChart.lastPeriod.description[props.type]),
        }),
      ],
      [
        EColumnPeriodComparisonConsumption.CURRENT_PERIOD_CONSUMPTION,
        ColumnSeriesAdapter({
          name: `${new Date().getFullYear()}`,
          colorGradient: currentPeriodConsumptionColumnColor.value,
          prefixSymbol: maybePrefixSymbol,
          accessibilityDescription: t(
            k.data.dashboard.consumptionComparisonChart.currentPeriod.description[props.type],
          ),
        }),
      ],
    ],
    yAxisAdapter: [
      [
        EColumnPeriodComparisonConsumption.LAST_PERIOD_CONSUMPTION,
        YAxisAdapter({
          prefixSymbol: maybePrefixSymbol,
        }),
      ],
      [
        EColumnPeriodComparisonConsumption.CURRENT_PERIOD_CONSUMPTION,
        YAxisAdapter({
          prefixSymbol: maybePrefixSymbol,
        }),
      ],
    ],
    plotOpts: {
      prefixSymbol: maybePrefixSymbol,
    },
    accessibilityDescription,
  };
});

function handleShowDsoConsumptionInfos() {
  isInfosDialogVisible.value = false;
  dashboardStore.showDsoConsumptionInfos();
}

function handleShowDevicesTotalConsumptionInfos() {
  isInfosDialogVisible.value = false;
  dashboardStore.showDevicesTotalConsumptionInfos();
}
</script>

<template>
  <DashboardCard
    v-model:infos-visible="isInfosDialogVisible"
    :infos="true"
    :title="t(k.data.dashboard.consumptionComparisonChart.title)"
  >
    <template #title-icon>
      <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
        <LineChartUpIcon />
      </div>
    </template>

    <template #header-right>
      <slot name="header-right" />
    </template>

    <template #infos>
      <i18n-t
        class="whitespace-pre-line"
        :keypath="keyOf(k.data.dashboard.consumptionComparisonChart.tooltip[props.source ?? 'fallback'])"
        tag="p"
      >
        <template #sitesConsumption>
          <HospitalityButton
            severity="link"
            size="xs"
            @click="handleShowDsoConsumptionInfos()"
            >{{ t(k.data.dashboard.insights.totalConsumption.title[props.type]) }}
          </HospitalityButton>
        </template>
        <template #equipmentConsumption>
          <HospitalityButton
            severity="link"
            size="xs"
            @click="handleShowDevicesTotalConsumptionInfos()"
            >{{ t(k.data.dashboard.insights.equipmentConsumption.title) }}
          </HospitalityButton>
        </template>
      </i18n-t>
    </template>

    <BaseColumnChart
      :column-chart-specs="columnChartSpecs"
      :has-no-data="props.hasNoData"
      :is-loading="props.isLoading"
      :navigable="true"
      :series="props.series"
    >
      <template #noDataMessage>
        <slot name="noDataMessage" />
      </template>
    </BaseColumnChart>
  </DashboardCard>
</template>
